const SESSION_SYNC_CHANNEL = 'arap-session-storage-sync-v1';
const SESSION_SYNC_REQUEST = 'SESSION_STORAGE_SYNC_REQUEST';
const SESSION_SYNC_RESPONSE = 'SESSION_STORAGE_SYNC_RESPONSE';
const SESSION_SYNC_TIMEOUT_MS = 500;

type SessionSyncRequest = {
  type: typeof SESSION_SYNC_REQUEST;
  requestId: string;
  sourceTabId: string;
};

type SessionSyncResponse = {
  type: typeof SESSION_SYNC_RESPONSE;
  requestId: string;
  sourceTabId: string;
  snapshot: Record<string, string>;
};

type SessionSyncMessage = SessionSyncRequest | SessionSyncResponse;

let channel: BroadcastChannel | null = null;
let initialized = false;
let pendingRequestId: string | null = null;
let pendingResolve: (() => void) | null = null;
const tabId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

function createSnapshot(): Record<string, string> {
  const snapshot: Record<string, string> = {};

  for (let i = 0; i < sessionStorage.length; i += 1) {
    const key = sessionStorage.key(i);
    if (!key) {
      continue;
    }

    const value = sessionStorage.getItem(key);
    if (value !== null) {
      snapshot[key] = value;
    }
  }

  return snapshot;
}

function hydrateSnapshot(snapshot: Record<string, string>): void {
  Object.entries(snapshot).forEach(([key, value]) => {
    sessionStorage.setItem(key, value);
  });
}

function isSyncMessage(data: unknown): data is SessionSyncMessage {
  return !!data && typeof data === 'object' && 'type' in (data as Record<string, unknown>);
}

function completePendingRequest(): void {
  if (pendingResolve) {
    pendingResolve();
  }

  pendingRequestId = null;
  pendingResolve = null;
}

function handleChannelMessage(event: MessageEvent): void {
  if (!isSyncMessage(event.data)) {
    return;
  }

  const message = event.data;

  if (message.type === SESSION_SYNC_REQUEST) {
    if (message.sourceTabId === tabId || !channel || sessionStorage.length === 0) {
      return;
    }

    const response: SessionSyncResponse = {
      type: SESSION_SYNC_RESPONSE,
      requestId: message.requestId,
      sourceTabId: tabId,
      snapshot: createSnapshot()
    };

    channel.postMessage(response);
    return;
  }

  if (
    message.type === SESSION_SYNC_RESPONSE &&
    message.requestId === pendingRequestId &&
    message.sourceTabId !== tabId
  ) {
    if (sessionStorage.length === 0 && Object.keys(message.snapshot).length > 0) {
      hydrateSnapshot(message.snapshot);
    }

    completePendingRequest();
  }
}

function setupChannel(): void {
  if (initialized || typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return;
  }

  channel = new BroadcastChannel(SESSION_SYNC_CHANNEL);
  channel.onmessage = handleChannelMessage;
  initialized = true;
}

export function initializeSessionStorageTabSync(): Promise<void> {
  setupChannel();

  const activeChannel = channel;

  if (!activeChannel || sessionStorage.length > 0) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const requestId = `${tabId}-${Date.now()}`;
    pendingRequestId = requestId;
    pendingResolve = resolve;

    const request: SessionSyncRequest = {
      type: SESSION_SYNC_REQUEST,
      requestId,
      sourceTabId: tabId
    };

    activeChannel.postMessage(request);

    setTimeout(() => {
      if (pendingRequestId === requestId) {
        completePendingRequest();
      }
    }, SESSION_SYNC_TIMEOUT_MS);
  });
}
